import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import styles from './LessonPreView.module.scss';
import '../customStyle.css';
import { Lesson, ExtendedUnit, ExtendedHeaUnit, ModeTypeEnum } from '@types';
import FSBackIcon from 'src/assets/icons/arrow_back.svg?react';
import CheckIcon from 'src/assets/icons/complete_check.svg?react';
import FSIcon from '@src/assets/icons/fullscreen.svg?react';
import Button from '@src/components/button/Button';
import SidebarUnit from './SidebarUnit';
import SelectedContent from '@src/components/program-details/selected-content/SelectedContent';
import { useCourseContent } from '@hooks/useCourseContent';
import { IconInfo, isHomeEDApp } from '@constants';
import { useAnalytics, Events, useUser } from '@hooks';
import { Section, StudyPlanContentFragment, useHeaConfirmLessonMutation } from '@src/api/graphql-codegen';
import Spinner from '@src/components/spinner/Spinner';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import { useMediaQuery } from 'react-responsive';
// import { Dropdown } from 'antd';
// import type { MenuProps } from 'antd';
import preview from '@src/assets/img/previewbar.svg?url';
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import SelectedNewLayoutContent from '@src/components/program-details/selected-content/SelectedNewLayoutContent';
import { TranslationBadge } from '@src/components/translation-provider';


type Props = {
  unit: ExtendedUnit | ExtendedHeaUnit;
  lesson: Lesson;
  onClose: () => void;
  onExit?: () => void;
  startLesson: (lesson: Lesson) => void;
  startExercise?: (exercise: Section, isFromPreview?: boolean) => void;
  isParentApp?: boolean;
  studyPlansForSelectedUnit?: StudyPlanContentFragment[];
  addUnitToStudyPlan?: () => void;
  studyMode?: string;
  isConfirmCompletion?: boolean;
  refetch?: (complete: boolean) => void;
  startShortClass?: (lesson: Lesson, isFromPreview?: boolean) => void;
  openDetails?: (lesson: Lesson) => void;
  handleResumeMenu?: (lesson: Lesson) => void;
};

function LessonDetailComponent(props: Props) {
  const {
    unit,
    lesson,
    onClose,
    onExit,
    startLesson,
    isParentApp,
    studyPlansForSelectedUnit,
    addUnitToStudyPlan,
    studyMode,
    isConfirmCompletion=false,
    refetch,
    // startShortClass,
    startExercise,
    openDetails,
    // handleResumeMenu,
  } = props;
  const [showBanner, setShowBanner] = useState(false)
  const {
    content: selectedContent,
    isPreview, setIsPreview,
    program, currentStudyplan,
    isNewLessonLayout,
  } = useCourseContent();
  const { trackEvent } = useAnalytics();
  const [user] = useUser();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const topbar = useRef<HTMLDivElement>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [previewHover, setPreviewHover] = useState(false);
  const [needFullscreen, setNeedFullscreen] = useState(false);
  // const [showLessonsMenu, setShowLessonsMenu] = useState(false)

  const isTablet = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent)
  }, []);
  const isMobile = useMediaQuery({ maxWidth: 1024 });

  const [heaConfirmLesson, {loading}] = useHeaConfirmLessonMutation();

  useEffect(() => {
    if (lesson?.id) {
      setShowBanner(true);
      setIsPreview(true);
      trackEvent(Events.actions.lessonPreviewOpen, {
        unit_name: unit.name || '',
        lesson_name: lesson?.name,
        course_name: program?.name || '',
        lesson_number: lesson?.number,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson?.id, setIsPreview, trackEvent, user])

  const handleConfirmComplete = useCallback(async() => {
    if (!currentStudyplan?.id || !lesson?.id) {
      return;
    }
    const res = await heaConfirmLesson({
      variables: {
        input: {
          studyplanId: currentStudyplan.id,
          lessonId: lesson.id,
          completed: true
        }
      }
    });
    if (res?.data?.heaConfirmLesson) {
      refetch?.(true);
    }
  },[currentStudyplan, lesson, heaConfirmLesson, refetch])

  const handleMenu = useCallback(() => {
    if (isTablet || isMobile) {
      setShowMenu(current => !current)
    } else {
      onClose()
    }
  }, [isMobile, isTablet, onClose]);

  // const handleLessonsVisible = useCallback((visible: boolean) => {
  //   setShowLessonsMenu(visible)
  // }, []);

  useEffect(() => {
    let timeout: any;

    const displayOpa = () => {
      if (topbar.current) {
        topbar.current.style.opacity = "1"
      }
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        if (topbar.current) {
          topbar.current.style.opacity = "0"
        }

      }, 5000);
    };
    let refWrapper: any = null;
    if (wrapperRef.current) {
      refWrapper = wrapperRef.current
      refWrapper.addEventListener('mousemove', displayOpa);
    }

    return () => {
      if (refWrapper) {
        refWrapper.removeEventListener('mousemove', displayOpa);
      }
    };

  }, [wrapperRef]);

  const listStyle = useMemo(() => {
    if (isTablet || isMobile) {
      return styles.listMenu;
    } else {
      return styles.list;
    }
  }, [isTablet, isMobile])

  const handleCloseMenu = () => {
    setShowMenu(false)
  }

  const screenWidth = useMemo(() => (isTablet || isMobile) ? -window.innerWidth : 0, [isMobile, isTablet]);
  const completedPercent = useMemo(() => lesson?.progress ? lesson.progress : 0, [lesson]);

  // const handleStartLesson = (e: any) => {
  //   if (e.domEvent) {
  //     e.domEvent.stopPropagation()
  //   } else {
  //     e.stopPropagation()
  //   }
  //   setShowLessonsMenu(false)
  //   startLesson(lesson)
  // }

  // const handleStartShortClass = (e: any) => {
  //   if (e.domEvent) {
  //     e.domEvent.stopPropagation()
  //   } else {
  //     e.stopPropagation()
  //   }
  //   setShowLessonsMenu(false)
  //   startShortClass?.(lesson, true);
  // }

  // const handleResume = () => {
  //   setShowLessonsMenu(false)
  //   handleResumeMenu?.(lesson)
  // }

  // const handleStart = () => {
  //   setShowLessonsMenu(false)
  //   startLesson?.(lesson)
  // }

  // const lessonsMenu: MenuProps['items'] = [
  //   {
  //     key: '1',
  //     label: (
  //       <div className={styles.dropDownMenuitem}>
  //         {'One Longer Class'}
  //       </div>
  //     ),
  //     onClick: (e) => handleStartLesson(e)
  //   },
  //   {
  //     key: '2',
  //     label: (
  //       <div className={styles.dropDownMenuitem}>
  //         {'Multiple Short Classes'}
  //       </div>
  //     ),
  //     onClick: (e) => handleStartShortClass(e)
  //   }
  // ]

  // const resumeMenu: MenuProps['items'] = [
  //   {
  //     key: '1',
  //     label: (
  //       <div className={styles.dropDownResumnMenuitem} onClick={handleResume}>
  //         <div className={styles.resumeMenuItem}>
  //           <div className={styles.resumeProgressBackBar}>
  //             <div className={styles.resumeProgressBar} style={{width: `${completedPercent}%`, backgroundColor: completedPercent === 100 ? "#62C590" : "#62C590", borderBottomRightRadius: completedPercent === 100 ? "3px" : "0" }}></div>
  //             <span className={styles.inprogressText}>Lesson in Progress</span>
  //           </div>
  //           <div className={styles.resumeText}>Resume Lesson</div>
  //         </div>
  //       </div>
  //     ),
  //   },
  //   {
  //     key: '2',
  //     label: (
  //       <div className={styles.dropDownMenuitem} onClick={handleStart}>
  //         Start from Beginning
  //       </div>
  //     ),
  //   }
  // ]

  const listSection = useMemo(() =>
    (<div className={listStyle} style={{transform: showMenu ? 'translateX(0)' : `translateX(${screenWidth}px)`, minWidth: isNewLessonLayout ? 'none' : 388, width: isNewLessonLayout ? '100%' : 'none'}}>
      { (isTablet || isMobile) && showMenu && <div className={styles.menuBar}>
        <button onClick={handleMenu} className={styles.menuButton} type={'button'}>
          <div className={styles.menuWrapper}>
            <CloseOutlined className={styles.burgerIcon}/>
          </div>
        </button>
        <button onClick={onClose} className={styles.closeButton} type={'button'}>
          <div className={styles.menuWrapper}>
            <FSBackIcon className={styles.closeIcon}/><div>Exit Lesson</div>
          </div>
        </button>
      </div>}
      <SidebarUnit unit={unit as any} lesson={lesson as Lesson} lastVisitedLesson={undefined} closeNav={handleCloseMenu} startLesson={startLesson} startExercise={startExercise}/>
    </div>),
    [handleMenu, isMobile, isTablet, lesson, listStyle, isNewLessonLayout, onClose, screenWidth, showMenu, unit, startLesson, startExercise]
  );

  return (
    <div ref={wrapperRef} className={styles.container}>
      {(isTablet || isMobile) && showMenu && <div className={styles.fsBackdrop} onClick={() => setShowMenu(false)}/>}
      <div className={styles.titleView}>
        {(isTablet || isMobile) && <button onClick={handleMenu} className={styles.menuButton} type={'button'}>
          <div className={styles.menuWrapper}>
            <MenuOutlined className={styles.burgerIcon}/>
          </div>
        </button>}
        <button onClick={onClose} className={styles.closeButton} type={'button'}>
          <div className={styles.menuWrapper}>
            <FSBackIcon className={styles.closeIcon}/>
          </div>
        </button>
        <div className={styles.titleSubView}>
          {(isParentApp || isHomeEDApp)
            ? (
              <div>
                {isHomeEDApp ? (
                  (studyMode === ModeTypeEnum.Edit || studyMode === ModeTypeEnum.Units)
                    ? (isTablet || isMobile) ? <div className={styles.subTitle}>You are in <span className={styles.boldPreview}>PREVIEW MODE</span>.</div>
                    : <div className={styles.subTitle}>You are in <span className={styles.boldPreview}>PREVIEW MODE</span>. To add this Unit to a Study Plan, click the <span className={styles.smallBoldPreview}>Plus Button</span>  👉🏽</div>
                    : isConfirmCompletion ? <div className={styles.clickTitle}>Click the arrow to return.</div>
                    : <div className={styles.subTitle}>You are in <span className={styles.boldPreview}>PREVIEW MODE</span>.</div>
                ) : <div className={styles.title}>{lesson.name}</div>}
              </div>
            ) : (
              isNewLessonLayout ? 
              <div className={styles.previewTitle}>{lesson.name}</div> :
              <div className={styles.previewTitle}>Lesson Preview</div>
            )
          }
        </div>
        {isParentApp ? (
          (isHomeEDApp && (studyPlansForSelectedUnit || []).length > 0) ? (
            <div className={styles.studyPlansListContainer}>
              {(studyPlansForSelectedUnit || []).map((studyPlan, index) => (
                <div key={index} className={styles.subItem} style={{background: `${studyPlan.darkColor}`}}>
                  <div className={styles.subItemTitle}>{studyPlan.name.startsWith('Parent') ? 'PC' : studyPlan.name.substring(0, 2).toLocaleUpperCase()}</div>
                </div>
              ))}
              <Button label='+' color='white' size='small' className={styles.addStudyPlanBtn} onClick={addUnitToStudyPlan} />
            </div>
          ): null
        ) : (
          isHomeEDApp && (studyMode === ModeTypeEnum.Edit || studyMode === ModeTypeEnum.Units) ? (
            <Button label='+' color='white' size='small' className={styles.addStudyPlanBtn} onClick={addUnitToStudyPlan} />
          ) : (
            isHomeEDApp ? isConfirmCompletion
            ? <Button disabled={loading} size={'extraSmall'} onClick={handleConfirmComplete} color={'lightgreen'} label={'Complete'} className={styles.completeButton} leftIcon={loading ? <Spinner width={20} height={20} /> : <CheckIcon />}/>
            : <Button label={'Begin Lesson'} color='white' className={styles.viewLesson} onClick={() => openDetails?.(lesson)} />
            : lesson.alternativeActivated || (100 > completedPercent && completedPercent > 0) ?
              isNewLessonLayout ? (
                <button onClick={()=>{setNeedFullscreen(true)}} className={styles.fullScreenButton} type={'button'}>
                  <div className={styles.iconWrapper}>
                    <FSIcon/>
                  </div>
                  {(!isMobile) && <span className={styles.textWhite}>Open full screen</span>}
                </button>
                ) : null : isNewLessonLayout ? (
                <button onClick={()=>{setNeedFullscreen(true)}} className={styles.fullScreenButton} type={'button'}>
                  <div className={styles.iconWrapper}>
                    <FSIcon/>
                  </div>
                  {(!isMobile) && <span className={styles.textWhite}>Open full screen</span>}
                </button>
              ) : <div>
                  {!user?.id?.toLowerCase().startsWith("au") && (
                    <TranslationBadge type="preview" />
                  )}
              </div>
          )
        )}
      </div>
      <div className={isNewLessonLayout ? styles.newPage : styles.page}>
        {listSection}
        <div className={styles.content}>
          {!isNewLessonLayout && showBanner && !isHomeEDApp ? <div className={styles.previewBanner}>
            <img src={preview} alt={'preview'} />
            <div className={styles.previewBar}>
              <span>PREVIEW</span>
              <div
                className={styles.iconWrapper}
                onMouseOver={() => setPreviewHover(true)}
                onMouseLeave={() => setPreviewHover(false)}
              >
                <LordIcon
                  src={IconInfo}
                  isHover={true}
                  colors={{ primary: "#FFFFFF", secondary: "#FFFFFF" }}
                  size={24}
                />
              </div>
            </div>
          </div> : null}
          {previewHover ? <div className={styles.previewTooltip}>
              <strong>Note:&nbsp;</strong>“Preview” watermark is removed when you begin the Lesson
          </div> : null}
          {isHomeEDApp || isParentApp ?
            showBanner && !isParentApp ? 
              <div className={styles.preview}><strong>Note:&nbsp;</strong>"“Preview” watermark is removed when you Activate a Study Plan"</div> : null
            : null
          }
          {selectedContent ?
            !isNewLessonLayout ?
              <SelectedContent content={selectedContent} lesson={lesson} unit={unit} goBack={onClose} onExit={onExit} isPreview={isPreview} showBanner={showBanner && !isParentApp} isParentApp={isParentApp} startLesson={startLesson} /> :
              <SelectedNewLayoutContent content={selectedContent} lesson={lesson} unit={unit} goBack={onClose} onExit={onExit} isPreview={isPreview} showBanner={showBanner && !isParentApp} isParentApp={isParentApp} startLesson={startLesson} needFullscreen={needFullscreen} closeFullScreen={()=>{setNeedFullscreen(false)}}/>
            : null
          }
        </div>
      </div>
    </div>
  );
}

const LessonPreView = (props: Props) => {

  const {
    unit,
    lesson,
    onClose,
    onExit,
    startLesson,
    isParentApp,
    studyPlansForSelectedUnit,
    addUnitToStudyPlan,
    studyMode,
    isConfirmCompletion,
    refetch,
    startShortClass,
    startExercise,
    openDetails,
    handleResumeMenu,
  } = props;

  return (
    <LessonDetailComponent
      unit={unit} lesson={lesson} onClose={onClose} onExit={onExit} startLesson={startLesson}
      isParentApp={isParentApp} startExercise={startExercise} handleResumeMenu={handleResumeMenu}
      studyPlansForSelectedUnit={studyPlansForSelectedUnit} openDetails={openDetails}
      addUnitToStudyPlan={addUnitToStudyPlan} startShortClass={startShortClass}
      studyMode={studyMode} isConfirmCompletion={isConfirmCompletion} refetch={refetch}
    />
  );
}

export default LessonPreView;
